import type React from 'react';
import {useState} from 'react';
import {Box, Button, Checkbox, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography} from '@mui/material';
import {Tune} from '@mui/icons-material';
import {SegmentedControl} from '~/components/segmented-control/SegmentedControl';
import type {PlanSectionKey, ToolKey} from '../../../types/strategyTypes';
import type {PlanProps} from '../strategyProps';
import {PlanSection} from '../PlanSection';
import {HelpTitle} from '../SectionLinks';

const GUIDE = 'Six analyses that shape the strategy, each drafted from this category’s data. Pick one with the switch '
    + 'below; drag points on the charts or click options to change them, and every change saves (Undo in the notice). '
    + 'Turn analyses on or off for this category under Analyses: one that is off is hidden and left out of the '
    + 'plan’s figures and checks.';

/** What each analysis is for and what it feeds, shown in the guide for the analysis on screen. */
const TOOL_GUIDE: Record<ToolKey, string> = {
    windmill: 'Dutch Windmill: place the category on Kraljic (supply risk against profit impact), and the suppliers on '
        + 'how they see you as a customer. Together they give a read per supplier. Add or remove suppliers below the table.',
    wheel: 'Sourcing wheel: click the ways of sourcing you will use; those that suit the Kraljic quadrant are marked. '
        + 'Selected ones appear under Key commercial levers.',
    power: 'Power: drag the top suppliers by how much you depend on them and they on you, to judge negotiating room.',
    pick: 'PICK: drag opportunities by pay-off and difficulty into Implement, Challenge, Possible or Kill, and click a '
        + 'value to set your own. It feeds Target benefits, the roadmap and the savings in the summary.',
    appraisal: 'Option appraisal: weight the business requirements and score each option 1-5; the highest score is the '
        + 'preferred option under Opportunities and options.',
    cube: 'Strategy cube: click the moves the strategy will make; they appear under Key commercial levers.',
};
import {AppraisalTool} from '../tools/AppraisalTool';
import {PickTool} from '../tools/PickTool';
import {PowerTool} from '../tools/PowerTool';
import {SourcingWheelTool} from '../tools/SourcingWheelTool';
import {StrategyCubeTool} from '../tools/StrategyCubeTool';
import {WindmillTool} from '../tools/WindmillTool';

const TOOLS: {key: ToolKey; sections: PlanSectionKey[]; Tool: React.FC<PlanProps>}[] = [
    {key: 'windmill', sections: ['kraljic', 'preferencing'], Tool: WindmillTool},
    {key: 'wheel', sections: ['wheel'], Tool: SourcingWheelTool},
    {key: 'power', sections: ['power'], Tool: PowerTool},
    {key: 'pick', sections: ['pick'], Tool: PickTool},
    {key: 'appraisal', sections: ['appraisal'], Tool: AppraisalTool},
    {key: 'cube', sections: ['cube'], Tool: StrategyCubeTool},
];

/**
 * Strategy formulation: the six Stage 4 analyses behind one switch. The team turns analyses on or off per category;
 * an analysis that is off is hidden here and left out of the plan’s figures and checks.
 */
export const TOOLS_SECTION_ID = 'plan-strategy-formulation';

type Props = PlanProps & {selected: ToolKey; onSelect: (key: ToolKey) => void};

export const ToolsSection: React.FC<Props> = ({selected, onSelect, ...props}) => {
    const {plan, actions} = props;
    const [menu, setMenu] = useState<HTMLElement | null>(null);
    const off = new Set(plan.derived.toolsOff);
    const label = (key: ToolKey) => plan.reference.tools.find(r => r.key === key)?.label ?? key;
    const visible = TOOLS.filter(t => !off.has(t.key));
    const current = visible.find(t => t.key === selected) ?? visible[0];

    const toggle = (key: ToolKey) => {
        const next = off.has(key) ? [...off].filter(k => k !== key) : [...off, key];
        void actions.save('toolsOff', next, `${label(key)} turned ${off.has(key) ? 'on' : 'off'}.`);
    };

    return (
        <Box id={TOOLS_SECTION_ID} sx={{scrollMarginTop: 16}}>
            <PlanSection
                title={<HelpTitle title="Strategy formulation" links={[]}
                                  help={current ? `${GUIDE} ${TOOL_GUIDE[current.key]}` : GUIDE}/>}
                edited={TOOLS.some(t => t.sections.some(s => plan.edited.includes(s))) || plan.edited.includes('toolsOff')}
                actions={plan.canEdit && (
                    <Button size="small" startIcon={<Tune/>} onClick={e => setMenu(e.currentTarget)}
                            aria-haspopup="menu" aria-expanded={Boolean(menu)}>
                        {`Analyses ${visible.length}/${TOOLS.length}`}
                    </Button>
                )}
            >
                <Stack gap={2}>
                    {current ? (
                        <>
                            <SegmentedControl labels={visible.map(t => label(t.key))} value={visible.indexOf(current)}
                                              onChange={i => onSelect(visible[i].key)}
                                              sx={{alignSelf: 'flex-start', flexWrap: 'wrap'}}/>
                            <current.Tool {...props}/>
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Every analysis is turned off for this category. Turn them on under Analyses.
                        </Typography>
                    )}
                </Stack>
                <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', px: 2, pb: 0.5, maxWidth: 280}}>
                        Analyses used for this category. One that is off is hidden and left out of the plan&apos;s figures and checks.
                    </Typography>
                    {TOOLS.map(t => (
                        <MenuItem key={t.key} onClick={() => toggle(t.key)} dense>
                            <ListItemIcon><Checkbox edge="start" size="small" checked={!off.has(t.key)} tabIndex={-1} disableRipple/></ListItemIcon>
                            <ListItemText primary={label(t.key)}/>
                        </MenuItem>
                    ))}
                </Menu>
            </PlanSection>
        </Box>
    );
};
